import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { CloudDownload, Loader2, RefreshCcw } from 'lucide-react';
import api from '../../../utils/axios';
import { Button } from '../../../components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../../components/ui/dialog';
import { fmtNum } from './kpiUtils';

const Dato = ({ label, valor, testId, fuerte }) => (
  <div className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-sm" data-testid={testId}>
    <span className="text-slate-600">{label}</span>
    <span className={`tabular-nums ${fuerte ? 'font-bold text-slate-900' : 'font-semibold'}`}>{fmtNum(valor)}</span>
  </div>
);

const ResumenFreshdesk = ({ r }) => (
  <div className="space-y-2">
    <Dato label="Tickets traídos de Freshdesk" valor={r.total} testId="a5-fd-total" fuerte />
    <Dato label="Nuevos" valor={r.nuevos} testId="a5-fd-nuevos" />
    <Dato label="Ya guardados que se actualizan" valor={r.actualizados} testId="a5-fd-actualizados" />
    <Dato label="… de esos, con cambios" valor={r.con_cambios} testId="a5-fd-con-cambios" />
    <Dato label="Pasan de abierto a cerrado" valor={r.pasan_a_cerrado} testId="a5-fd-cerrados" />
    {r.omitidos_mas_viejos > 0 && <Dato label="No se pisan (el guardado es más nuevo)" valor={r.omitidos_mas_viejos} testId="a5-fd-omitidos" />}
  </div>
);

const ResumenRecalculo = ({ r }) => (
  <div className="space-y-2">
    <Dato label={`Tickets que cambian (de ${fmtNum(r.total)})`} valor={r.cambian} testId="a5-rc-cambian" fuerte />
    <Dato label="Cambian de grupo" valor={r.por_tipo?.grupo} testId="a5-rc-grupo" />
    <Dato label="Cambian de canal" valor={r.por_tipo?.canal} testId="a5-rc-canal" />
    <Dato label="Cambian de motivo" valor={r.por_tipo?.motivo} testId="a5-rc-motivo" />
    <Dato label="Cambian de sistema (excluidos o no)" valor={r.por_tipo?.sistema} testId="a5-rc-sistema" />
    <Dato label="Fechas pasadas a hora Argentina" valor={r.por_tipo?.fechas} testId="a5-rc-fechas" />
    {(r.ejemplos || []).length > 0 && (
      <div className="max-h-40 overflow-auto rounded-lg border border-slate-100 p-2 text-xs text-slate-600" data-testid="a5-rc-ejemplos">
        {r.ejemplos.map((e) => (
          <p key={e.ticket_id}>#{e.ticket_id}: {Object.entries(e).filter(([k]) => k !== 'ticket_id').map(([k, [a, b]]) => `${k} ${a ?? '—'} → ${b}`).join(' · ')}</p>
        ))}
      </div>
    )}
  </div>
);

// A5: acciones solo para ADMINISTRADOR, siempre con vista previa y Confirmar. Si la vista previa venció,
// Confirmar la vuelve a calcular y la aplica en el mismo paso, y muestra el resultado.
export const AtencionAccionesAdmin = ({ onDone }) => {
  const [dlg, setDlg] = useState(null);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);

  const fallo = (e, msg) => setDlg((d) => d && { ...d, cargando: false, guardando: false, aplicando: false, error: e.response?.data?.detail || msg });

  const sondear = (jobId, alTerminar) => {
    timer.current = setTimeout(async () => {
      try {
        const r = await api.get(`/kpis/atencion/freshdesk/preview/${jobId}`);
        if (r.data.estado === 'corriendo') return sondear(jobId, alTerminar);
        alTerminar(r.data);
      } catch (e) { fallo(e, 'Error al consultar Freshdesk'); }
    }, 3000);
  };

  const abrir = async (tipo) => {
    setDlg({ tipo, cargando: true });
    try {
      if (tipo === 'freshdesk') {
        const r = await api.post('/kpis/atencion/freshdesk/preview', null, { params: { dias: 120 } });
        sondear(r.data.job_id, (j) => setDlg((d) => d && { ...d, cargando: false, data: j.resumen, error: j.error, jobId: r.data.job_id }));
      } else {
        const r = await api.post('/kpis/atencion/recalcular/preview', null, { timeout: 120000 });
        setDlg({ tipo, cargando: false, data: r.data, previewId: r.data.preview_id });
      }
    } catch (e) {
      setDlg({ tipo, cargando: false, error: e.response?.data?.detail || 'No se pudo generar la vista previa' });
    }
  };

  const terminar = (res, recalculada) => {
    toast.success(dlg.tipo === 'freshdesk'
      ? `Listo: ${fmtNum(res.nuevos)} nuevos y ${fmtNum(res.actualizados)} actualizados`
      : `Listo: ${fmtNum(res.aplicados)} tickets recalculados`);
    if (res.backup) toast.message(`Backup previo: ${res.backup.coleccion}`);
    onDone?.();
    if (recalculada) setDlg((d) => d && { ...d, guardando: false, aplicando: false, error: null, data: res, aplicado: true });
    else setDlg(null);
  };

  const confirmar = async () => {
    setDlg((d) => ({ ...d, guardando: true, error: null }));
    try {
      if (dlg.tipo === 'freshdesk') {
        const r = await api.post(`/kpis/atencion/freshdesk/confirmar/${dlg.jobId}`, null, { params: { dias: 120 }, timeout: 180000 });
        if (!r.data.recalculada) return terminar(r.data, false);
        setDlg((d) => d && { ...d, aplicando: true });
        sondear(r.data.job_id, (j) => (j.estado === 'error'
          ? setDlg((d) => d && { ...d, guardando: false, aplicando: false, error: j.error || 'No se pudo aplicar' })
          : terminar(j.resultado, true)));
      } else {
        const r = await api.post('/kpis/atencion/recalcular/confirmar', null, { params: { preview_id: dlg.previewId }, timeout: 180000 });
        terminar(r.data, r.data.recalculada);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo confirmar');
      setDlg((d) => ({ ...d, guardando: false }));
    }
  };

  const titulo = dlg?.tipo === 'freshdesk' ? 'Traer últimos 120 días de Freshdesk' : 'Recalcular campos de tickets';
  const sinCambios = dlg?.data && (dlg.tipo === 'freshdesk' ? !dlg.data.total : !dlg.data.cambian);
  return (
    <>
      <Button variant="outline" onClick={() => abrir('freshdesk')} data-testid="atencion-traer-120-btn" className="border-sky-200 text-sky-700 hover:bg-sky-50">
        <CloudDownload className="mr-1 h-4 w-4" /> Traer últimos 120 días de Freshdesk
      </Button>
      <Button variant="outline" onClick={() => abrir('recalcular')} data-testid="atencion-recalcular-btn" className="border-slate-300 text-slate-700">
        <RefreshCcw className="mr-1 h-4 w-4" /> Recalcular campos de tickets
      </Button>
      <Dialog open={!!dlg} onOpenChange={(o) => !o && !dlg?.guardando && setDlg(null)}>
        <DialogContent className="max-w-lg" data-testid="atencion-a5-dialog">
          <DialogHeader>
            <DialogTitle>{titulo}</DialogTitle>
            <DialogDescription>
              {dlg?.aplicado ? 'Resultado de lo que se aplicó.' : 'Vista previa: todavía no se escribió nada. Al confirmar se hace un backup de los tickets y se actualizan por número de ticket, sin borrar los demás.'}
            </DialogDescription>
          </DialogHeader>
          {dlg?.aplicado && (
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="atencion-a5-recalculada">
              La vista previa había vencido: se volvió a calcular y ya se aplicó.
            </p>
          )}
          {(dlg?.cargando || dlg?.aplicando) && (
            <p className="flex items-center gap-2 py-6 text-sm text-slate-500" data-testid="atencion-a5-cargando">
              <Loader2 className="h-4 w-4 animate-spin" /> {dlg.aplicando ? 'La vista previa venció: volviendo a traer de Freshdesk y aplicando (puede tardar un par de minutos)…'
                : dlg.tipo === 'freshdesk' ? 'Consultando Freshdesk (puede tardar un par de minutos)…' : 'Calculando…'}
            </p>
          )}
          {dlg?.error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="atencion-a5-error">{dlg.error}</p>}
          {dlg?.data && !dlg?.aplicando && (dlg.tipo === 'freshdesk' ? <ResumenFreshdesk r={dlg.data} /> : <ResumenRecalculo r={dlg.data} />)}
          <DialogFooter>
            {dlg?.aplicado ? (
              <Button onClick={() => setDlg(null)} data-testid="atencion-a5-cerrar" className="bg-sky-600 text-white hover:bg-sky-700">Cerrar</Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => setDlg(null)} disabled={dlg?.guardando} data-testid="atencion-a5-cancelar">Cancelar</Button>
                <Button onClick={confirmar} disabled={!dlg?.data || dlg?.guardando || sinCambios} data-testid="atencion-a5-confirmar" className="bg-sky-600 text-white hover:bg-sky-700">
                  {dlg?.guardando ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null} Confirmar
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
